import { Box } from "@mui/material"
import AppBar from "@mui/material/AppBar"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"
import Avatar from "@mui/material/Avatar"
import Tooltip from "@mui/material/Tooltip"
import { useState } from "react"
import Logo from "@/components/Logo"
import MenuItems from "./MenuItems"
import { useNavigate } from "react-router-dom"
import { confirmLeaveItemEditorIfNeeded } from "../component/itemEditorNavigationGuard"
import ProfileMenu from "../user/ProfileMenu"
import { useAuth } from "../auth/AuthContext"
import { Button } from "@/components/ui/button"
import {
  Settings,
  X
} from "lucide-react"
import { ThemeToggle } from "@/components/ThemeToggle"
import { useAppVersion } from "@/hooks/useAppVersion"

type ResponsiveAppBarProps = {
  title?: string | undefined
  mobileMenuOpen?: boolean
  setMobileMenuOpen?: (open: boolean) => void
}

function ResponsiveAppBar({ title, mobileMenuOpen, setMobileMenuOpen }: ResponsiveAppBarProps) {
  const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false)
  const { version: appVersion, releaseDate: appReleaseDate } = useAppVersion()

  const isControlled = typeof mobileMenuOpen === "boolean" && typeof setMobileMenuOpen === "function"
  const showMobileMenu = isControlled ? mobileMenuOpen : internalMobileMenuOpen
  const setShowMobileMenu = isControlled ? setMobileMenuOpen : setInternalMobileMenuOpen
  // const user = useSelector((state: any) => state.user)
  const { user } = useAuth();
  const userUid = user?.uid || null;
  const userUidLabel = userUid ? `UID: ${userUid}` : "UID unavailable"

  const navigate = useNavigate()
  const navHome = () => {
    if (!confirmLeaveItemEditorIfNeeded()) return
    navigate("/")
  }

  // Get user initials for avatar
  const getUserInitials = (email: string) => {
    if (!email) return "U"
    const parts = email.split("@")[0].split(".")
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return email[0].toUpperCase()
  }

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "white",
        color: "#000",
        boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        borderBottom: "1px solid #e5e7eb",
        "@media (prefers-color-scheme: dark)": {
          backgroundColor: "#1f2937",
          color: "#fff",
          borderBottom: "1px solid #374151",
        },
      }}
      className="dark:bg-gray-800 dark:text-white dark:border-gray-700"
    >
      <Box className="px-4 lg:px-6" sx={{ width: "100vw", margin: 0 }}>
        <Toolbar disableGutters className="min-h-16">
          {/* Desktop Logo and Title */}
          <div className="hidden md:flex items-center space-x-6 flex-1">
            <Logo size="md" withText={true} onClick={navHome} />

            {title && (
              <div className="flex items-center space-x-2">
                <div className="w-1 h-6 bg-gray-300 rounded-full"></div>
                <div>
                  <Typography
                    variant="h6"
                    className="font-semibold text-gray-700 dark:text-gray-300"
                    sx={{
                      fontFamily: "system-ui, -apple-system, sans-serif",
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                  >
                    {title}
                  </Typography>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Current workspace</div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Logo */}
          <div className="md:hidden flex items-center justify-start flex-1 pl-2">
            <Logo size="sm" withText={true} onClick={navHome} />
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-3">
            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Profile Section */}
            <ProfileMenu
              displayText={user?.email}
            >
              <div className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity">
                <div className="hidden md:block text-right">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user?.email?.split("@")[0] || "User"}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email || "user@example.com"}
                  </div>
                </div>

                <div className="relative">
                  <Tooltip
                    title={userUidLabel}
                    disableHoverListener={!userUid}
                    disableFocusListener={!userUid}
                    disableTouchListener={!userUid}
                    arrow
                    placement="bottom"
                  >
                    <Button
                      variant="ghost"
                      className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                      aria-label={userUid ? `User UID ${userUid}` : "User UID unavailable"}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#3b82f6",
                          width: 36,
                          height: 36,
                          fontSize: "0.875rem",
                          fontWeight: 600
                        }}
                      >
                        {getUserInitials(user?.email || "")}
                      </Avatar>
                    </Button>
                  </Tooltip>
                  <div className="absolute top-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
                </div>
              </div>
            </ProfileMenu>
          </div>
        </Toolbar>

        {/* Mobile Menu Overlay */}
        {showMobileMenu && (
          <div className="fixed inset-0 z-50 xl:hidden">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setShowMobileMenu(false)}
            />

            {/* Menu Panel */}
            <div className="fixed inset-y-0 left-0 w-80 max-w-sm bg-white dark:bg-slate-900 shadow-xl transform transition-transform flex flex-col">
              {/* Header with Labwick logo */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center space-x-3">
                  <Logo size="sm" withText={true} onClick={navHome} />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowMobileMenu(false)}
                  className="text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 p-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Navigation */}
              <div className="flex-1 overflow-y-auto scrollbar-gutter-stable">
                <MenuItems
                  mobile={true}
                  onNav={() => setShowMobileMenu(false)}
                />
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {appVersion
                      ? `v ${appVersion}${appReleaseDate ? ` · ${appReleaseDate}` : ""}`
                      : ""}
                  </div>
                  <Button variant="ghost" size="sm" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white p-2">
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Box>
    </AppBar>
  )
}

export default ResponsiveAppBar
